import { useCurrentOverlay } from "shared/context/overlay/OverlayIDContext"
import { useEvent } from "shared/lib/useEvent"
import { useFormSubmit } from "shared/lib/useFormSubmit"
import { ProjectTypename } from "shared/types/Project"

import { useDuplicateRoom } from "entities/rooms/api/useDuplicateRoom"
import { useRoomFragment } from "entities/rooms/model/useRoomFragment"
import { RoomFormData } from "features/room-management/types/RoomFormData"
import { useRoomForm } from "widgets/room/room-form/model/useRoomForm"
import { RoomOverlayModel } from "widgets/room/room-overlay/RoomOverlay"

import { CopyRoomOverlayProps } from "../CopyRoomOverlay"

export function useCopyRoomOverlayModel({
  payload: { projectID, roomID }
}: CopyRoomOverlayProps): Omit<RoomOverlayModel, "id" | "headline"> {
  const [duplicateRoom] = useDuplicateRoom()
  const room = useRoomFragment(roomID)

  const { closeOverlay } = useCurrentOverlay()

  const submitCopy = useEvent(async (roomFormData: RoomFormData) => {
    try {
      const result = await duplicateRoom({
        projectID: roomFormData.projectID || projectID,
        parentID: roomFormData.parentID ?? undefined,
        position: roomFormData.room?.position,
        roomID,
        parentType: ProjectTypename,
        label: roomFormData.room?.label ?? undefined
      })
      return { data: result ?? null }
    } catch (error) {
      return { data: null, errors: [{ message: String(error) }] }
    }
  })

  const { handleSubmit, isSubmitting } = useFormSubmit<RoomFormData>({
    onSubmit: submitCopy,
    onSuccess: closeOverlay
  })

  const roomFormModel = useRoomForm({
    onSubmit: handleSubmit,
    formOptions: { hideRemark: true, hideProject: false },
    room
  })

  return {
    roomFormModel,
    formID: "copy-room-form",
    isLoading: isSubmitting,
    isSubmitting,
    onCancel: closeOverlay
  }
}
